
import defaults from './defaults.json';

// Detect production environment from hostname
const isProduction = typeof window !== 'undefined' && 
  (window.location.hostname === 'blackkiteai.com' || 
   window.location.hostname === 'www.blackkiteai.com' ||
   window.location.hostname.endsWith('.blackkiteai.com'));

// Backend API base URL - use environment variable, auto-detect production, or default to localhost
export const BACKEND_URL = process.env.REACT_APP_API_BASE_URL
  || process.env.REACT_APP_BACKEND_URL
  || (isProduction ? 'https://api.blackkiteai.com' : defaults.DEFAULT_BACKEND_URL);

// Speechmatics Configuration - connects through backend WebSocket proxy
export const SPEECHMATICS_CONFIG = {
  // Get WebSocket URL for Speechmatics (via backend proxy)
  getWebSocketUrl: (language = 'en', sessionId = null, email = null) => {
    const backendUrl = BACKEND_URL;
    // Convert HTTP(S) URL to WS(S) URL
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    
    // Cookie-based auth: JWT is sent via cookie automatically.
    // No need to include token in the URL.
    let wsUrl = `${wsProtocol}://${wsHost}/ws/speechmatics?lang=${encodeURIComponent(language)}`;
    
    // Add sessionId and email for transcript tracking
    if (sessionId) wsUrl += `&sessionId=${encodeURIComponent(sessionId)}`;
    if (email) wsUrl += `&email=${encodeURIComponent(email)}`;
      
    console.log('[Speechmatics] WebSocket URL:', wsUrl, '| Language:', language, '| Backend URL:', backendUrl, '| Production:', isProduction);
    return wsUrl;
  },
};

export const API_ENDPOINTS = {
  AUTH: {
    GOOGLE: `${BACKEND_URL}/auth/google`,
    ME: `${BACKEND_URL}/api/auth/me`, // Get current user profile
    ADMIN_CHECK: `${BACKEND_URL}/api/auth/admin-check`, // Check if current user is admin
    CANDIDATE_MATCH_ACCESS_CHECK: `${BACKEND_URL}/api/auth/candidate-match-access-check`,
  },
  USERS: {
    BASE: `${BACKEND_URL}/api/users`,
    CREDITS: `${BACKEND_URL}/api/users/credits`,
    PROFILE: `${BACKEND_URL}/api/users/profile`,
    INCREMENT_TRIAL_SESSION: `${BACKEND_URL}/api/users/increment-trial-session`,
  },
  SUBSCRIPTIONS: {
    COUNT: `${BACKEND_URL}/api/subscriptions/count`,
  },
  PAYMENTS: {
    CONFIG: `${BACKEND_URL}/api/payments/config`,
    CREATE_ORDER: `${BACKEND_URL}/api/payments/create-order`,
    VERIFY: `${BACKEND_URL}/api/payments/verify`,
    FAILURE: `${BACKEND_URL}/api/payments/failure`,
    REFUND: `${BACKEND_URL}/api/payments/refund`,
  },
  COUPONS: {
    VALIDATE: `${BACKEND_URL}/api/coupons/validate`,
  },
  SESSIONS: {
    INSERT: (email) => `${BACKEND_URL}/api/sessions/insert?email=${encodeURIComponent(email)}`,
    BASE: `${BACKEND_URL}/api/sessions`,
    TRANSCRIPT: (sessionId) => `${BACKEND_URL}/api/sessions/${sessionId}/transcript`,
    TRANSCRIPT_EXISTS: (sessionId) => `${BACKEND_URL}/api/sessions/${sessionId}/transcript/exists`,
    TRANSCRIPT_FLUSH: (sessionId) => `${BACKEND_URL}/api/sessions/${sessionId}/transcript/flush`,
  },
  UPLOAD: {
    RESUME: `${BACKEND_URL}/api/upload-resume`,
  },
  DOWNLOAD: {
    LATEST_EXE: `${BACKEND_URL}/api/download/latest-exe`,
  },
  RESUMES: {
    DOWNLOAD: (resumeId) => `${BACKEND_URL}/api/resumes/download?resumeId=${resumeId}`,
    ADMIN_DOWNLOAD: (resumeId) => `${BACKEND_URL}/api/resumes/admin/download/${resumeId}`,
  },
  REFERRALS: {
    VALIDATE: `${BACKEND_URL}/api/referrals/validate`,
    MY_CODE: `${BACKEND_URL}/api/referrals/my-code`,
    HISTORY: `${BACKEND_URL}/api/referrals/history`,
  },
  CAMPAIGNS: {
    LIST: `${BACKEND_URL}/api/campaigns`,
    CREATE: `${BACKEND_URL}/api/campaigns`,
    DETAIL: (id) => `${BACKEND_URL}/api/campaigns/${id}`,
    UPDATE: (id) => `${BACKEND_URL}/api/campaigns/${id}`,
    DELETE: (id) => `${BACKEND_URL}/api/campaigns/${id}`,
    SEND: (id) => `${BACKEND_URL}/api/campaigns/${id}/send`,
    ANALYTICS: (id) => `${BACKEND_URL}/api/campaigns/${id}/analytics`,
    RECIPIENTS: (id) => `${BACKEND_URL}/api/campaigns/${id}/recipients`,
    EVENTS: (id) => `${BACKEND_URL}/api/campaigns/${id}/events`,
  },
  JOBS: {
    LIST: `${BACKEND_URL}/api/jobs`,
    FILTERS: `${BACKEND_URL}/api/jobs/filters`,
    DETAIL: (id) => `${BACKEND_URL}/api/jobs/${id}`,
    ADMIN_LIST: `${BACKEND_URL}/api/admin/jobs`,
    ADMIN_CREATE: `${BACKEND_URL}/api/admin/jobs`,
    ADMIN_UPDATE: (id) => `${BACKEND_URL}/api/admin/jobs/${id}`,
    ADMIN_DELETE: (id) => `${BACKEND_URL}/api/admin/jobs/${id}`,
    ADMIN_BULK_DELETE: `${BACKEND_URL}/api/admin/jobs/bulk`,
    ADMIN_UPLOAD_EXCEL: `${BACKEND_URL}/api/admin/jobs/upload-excel`,
  },
  CANDIDATE_MATCHES: {
    ADMIN_LIST: `${BACKEND_URL}/api/admin/candidate-matches`,
    ADMIN_UPLOAD: `${BACKEND_URL}/api/admin/candidate-matches/upload`,
    ADMIN_DETAIL: (batchId) => `${BACKEND_URL}/api/admin/candidate-matches/${batchId}`,
    ADMIN_SHORTLISTED: (batchId) => `${BACKEND_URL}/api/admin/candidate-matches/${batchId}/shortlisted`,
    ADMIN_REPORT: (batchId) => `${BACKEND_URL}/api/admin/candidate-matches/${batchId}/report`,
  },
};

// Helper function to build full API URL
export const buildApiUrl = (path) => {
  const base = path.startsWith('/') ? BACKEND_URL : `${BACKEND_URL}/`;
  return `${base}${path}`;
};

const apiConfig = {
  BACKEND_URL,
  API_ENDPOINTS,
  buildApiUrl,
};

export default apiConfig;
